/**
 * A download link may carry several purchases' tokens at once, comma-separated
 * (`?token=a,b`).
 *
 * Why: an athlete can buy from the same album more than once (a second search
 * finds a photo the first did not, a pre-purchase is redeemed beside a later
 * purchase), and each payment is its own row with its own token and its own
 * photos. The hub used to open on the newest token only, so the photos paid for
 * in every earlier purchase disappeared from "your photos" while a fresh search
 * still found them. The hub, which has proved who the athlete is, now hands the
 * finder every working token it holds for that album, and the download routes
 * deliver the union.
 *
 * Nothing about authorisation changes: each token is still a bearer secret for
 * its own purchase and is checked on its own (download-access.ts). Holding two
 * tokens has always meant being able to download both purchases; this only
 * saves asking twice. Tokens are nanoid(32), so a comma never occurs in one.
 */

/** More than any real athlete holds; bounds the query for a hand-made URL. */
export const MAX_DOWNLOAD_TOKENS = 20;

/** The tokens in a `token` query value, trimmed, de-duplicated, capped. */
export function parseDownloadTokens(param: string | null | undefined): string[] {
  if (!param) return [];
  const seen = new Set<string>();
  for (const raw of param.split(',')) {
    const t = raw.trim();
    if (t && !seen.has(t)) seen.add(t);
    if (seen.size >= MAX_DOWNLOAD_TOKENS) break;
  }
  return [...seen];
}

/** The `token` query value for a set of tokens, or null when there are none. */
export function joinDownloadTokens(tokens: (string | null | undefined)[]): string | null {
  const list = parseDownloadTokens(tokens.filter((t): t is string => !!t).join(','));
  return list.length > 0 ? list.join(',') : null;
}

/**
 * Every photo the given purchases hold, each once, in purchase order (the first
 * purchase's photos first). Empty ids are dropped.
 */
export function purchasedPhotoIds(purchases: { matchedPhotoIds: string[] | null }[]): string[] {
  const seen = new Set<string>();
  for (const p of purchases) {
    for (const id of p.matchedPhotoIds || []) {
      if (id) seen.add(id);
    }
  }
  return [...seen];
}
